import React, { useState } from "react";
import { Platform } from "react-native";
import analytics from "../../analytics";
import { eventLivePageBase } from "../globalUtils";
import { CopyButton } from "@/components/settings/Button";
import CodeBox from "@/components/settings/CodeBox";
import {
  ChoiceGroup,
  RowGroup,
  ToggleRow,
} from "@/components/settings/SettingRow";
import {
  SettingsPage,
  SettingsSection,
} from "@/components/settings/SettingsPage";
import EmbedEmailLinks from "./EmbedEmailLinks";
import EmbedPreview from "./EmbedPreview";
import { previewLanguage, t } from "@/localization";

// The embed code goes on the broadcaster's own website, so it stays English.
const { eventEmbedCode, eventFrameSrc } = require("./embedCodes.cjs");

if (typeof URLSearchParams.prototype.set !== "function") {
  URLSearchParams.prototype.set = function (name, value) {
    let params = Array.from(this.entries());
    let found = false;
    params = params.map(([n, v]) => {
      if (n === name) {
        found = true;
        return [n, value];
      }
      return [n, v];
    });
    if (!found) {
      params.push([name, value]);
    }
    return new URLSearchParams(params);
  };
}

const EventEmbedForm = ({ event }) => {
  const [embedStyle, setEmbedStyle] = useState(`embed`);
  const [autoplay, setAutoplay] = useState(true);
  const [liveOnly, setLiveOnly] = useState(false);
  const [reminders, setReminders] = useState(true);
  const [codeType, setCodeType] = useState("script");

  const options = {
    base: eventLivePageBase,
    broadcaster: event.broadcaster,
    slug: event.slug,
    style: embedStyle,
    autoplay,
    reminders,
    liveOnly,
  };

  // Todo: support alias
  const embedCode = eventEmbedCode({ ...options, type: codeType });
  // The preview shows the page in the app's language; the code people copy
  // doesn't pin one, so each guest's browser decides.
  const lang = previewLanguage();

  const trackCopy = () =>
    analytics.track("Copy Embed", {
      liveOnly,
      embedStyle,
      autoplay,
      codeType,
    });

  return (
    <SettingsPage
      title={t("Embed")}
      description={t(
        "Put your event page or just the video player on your own website."
      )}
    >
      <SettingsSection first title={t("Layout")} help="embed.layout">
        <ChoiceGroup
          accessibilityLabel={t("Embed layout")}
          value={embedStyle}
          onChange={setEmbedStyle}
          options={[
            {
              value: "embed",
              label: t("Full embed"),
              description: t("The event page: video, guestbook and reminders."),
            },
            {
              value: "embedBare",
              label: t("Just video"),
              description: t("Only the video player."),
            },
          ]}
        />
      </SettingsSection>

      <SettingsSection
        title={t("Code options")}
        help="embed.options"
        description={t(
          "These change the code below. Copy it again after changing them."
        )}
      >
        <RowGroup>
          <ToggleRow
            label={t("Autoplay")}
            description={t("Start the video automatically.")}
            value={autoplay}
            onValueChange={setAutoplay}
          />
          <ToggleRow
            label={t("Reminder button")}
            description={t(
              "Guests can sign up for reminders from your website."
            )}
            value={reminders}
            onValueChange={setReminders}
          />
          <ToggleRow
            label={t("Live only")}
            description={t("No replay and no rewind in the embed.")}
            value={liveOnly}
            onValueChange={setLiveOnly}
          />
        </RowGroup>
      </SettingsSection>

      <SettingsSection title={t("Code type")} help="embed.codeType">
        <ChoiceGroup
          accessibilityLabel={t("Code type")}
          value={codeType}
          onChange={setCodeType}
          options={[
            {
              value: "script",
              label: t("Script (recommended)"),
              description: t(
                "Viewers who open an EventLive email link on your page are recognized there, and the player is sized to fit."
              ),
            },
            {
              value: "iframe",
              label: t("Simple iframe (no script)"),
              description: t(
                "For site builders that remove scripts. The player can't see viewers' email links, so ticket holders on a new device may have to sign in again."
              ),
            },
          ]}
        />
      </SettingsSection>

      <SettingsSection title={t("Embed code")}>
        <CodeBox code={embedCode} />
        <CopyButton
          value={embedCode}
          text={t("Copy embed code")}
          copiedText={t("Embed code copied")}
          toast={t("Embed code copied")}
          toastDetail={t("Paste it into your website's HTML.")}
          variant="primary"
          onCopy={trackCopy}
        />
      </SettingsSection>

      <EmbedEmailLinks slug={event.slug} />

      {Platform.OS === "web" && (
        <SettingsSection title={t("Preview")}>
          {/* The script code's frame fits its content; the simple iframe
              keeps the fixed size it has on a website. */}
          {codeType === "script" ? (
            <EmbedPreview
              src={eventFrameSrc({ ...options, fit: true, lang })}
            />
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html: eventEmbedCode({ ...options, type: codeType, lang }),
              }}
            ></div>
          )}
        </SettingsSection>
      )}
    </SettingsPage>
  );
};

export default EventEmbedForm;
